import React, { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { TrainerMemberService } from '../lib/trainerMemberService';
import { sendConnectionInvite } from '../lib/inviteEmailService';
import { shouldAttemptInviteEmail } from '../lib/memberInviteLogic';
import {
  addMemberModalClose,
  addMemberModalSetForm,
  addMemberModalSetPatch,
  getAddMemberModalSnapshot,
  subscribeAddMemberModal,
} from '../lib/addMemberModalStore';
import { X } from '../features/trainer/bravosIcons';
import '../features/trainer/dashboard/members/MembersManagement.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

interface AddMemberModalLayerProps {
  trainerId: string;
  trainerName?: string;
  onReloadMembers: () => Promise<void>;
  /** Fired after a successful invite (relationship or email-only). */
  onInviteSuccess?: (email: string) => void | Promise<void>;
}

/**
 * Invite-member modal (design: members-management-design-reference.html).
 * Store subscription keeps MemberManagement from re-rendering on open.
 */
export const AddMemberModalLayer: React.FC<AddMemberModalLayerProps> = ({
  trainerId,
  trainerName,
  onReloadMembers,
  onInviteSuccess,
}) => {
  const snap = useSyncExternalStore(subscribeAddMemberModal, getAddMemberModalSnapshot, getAddMemberModalSnapshot);
  const reloadRef = useRef(onReloadMembers);
  reloadRef.current = onReloadMembers;
  const successRef = useRef(onInviteSuccess);
  successRef.current = onInviteSuccess;
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [invalid, setInvalid] = React.useState(false);

  const close = useCallback(() => {
    setInvalid(false);
    addMemberModalClose();
  }, []);

  useEffect(() => {
    if (!snap.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    if (snap.modal.step === 'form') {
      window.setTimeout(() => emailInputRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('keydown', onKey);
  }, [snap.open, snap.modal.step, close]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailToSend = getAddMemberModalSnapshot().memberEmail.trim();
    if (!EMAIL_PATTERN.test(emailToSend)) {
      setInvalid(true);
      addMemberModalSetPatch({ createError: null });
      emailInputRef.current?.focus();
      return;
    }
    setInvalid(false);
    try {
      addMemberModalSetPatch({ isCreating: true, createError: null });
      const result = await TrainerMemberService.inviteMemberByEmail(trainerId, emailToSend, {
        status: 'onboarding',
      });

      if (result.kind === 'already_connected') {
        addMemberModalSetPatch({
          modal: { step: 'already_connected', email: emailToSend },
          isCreating: false,
        });
        await reloadRef.current();
        return;
      }

      let emailWarning: string | null = null;
      if (shouldAttemptInviteEmail()) {
        try {
          await sendConnectionInvite({
            to: emailToSend,
            trainerName: trainerName || 'Your trainer',
            trainerId,
            relationshipId: result.kind === 'relationship' ? result.relationship.id : null,
          });
        } catch (emailErr: any) {
          console.warn('Invitation email could not be sent:', emailErr);
          let msg = emailErr?.message || 'We could not send the invitation email.';
          if (msg.includes('only send testing emails') || msg.includes('verify a domain')) {
            msg +=
              ' To send to any address: verify a domain at resend.com/domains and set the FROM_EMAIL secret in Supabase (Edge Functions → Secrets).';
          }
          emailWarning = msg;
        }
      } else {
        emailWarning =
          'Email sending is disabled for this environment (REACT_APP_SEND_INVITE_EMAIL=false). The invite is still saved.';
      }

      const isResend = result.kind === 'relationship' ? result.isResend : result.isResend;
      addMemberModalSetPatch({
        modal: {
          step: 'success',
          email: emailToSend,
          emailWarning,
          showResendHint: isResend && !emailWarning,
        },
        isCreating: false,
      });
      await successRef.current?.(emailToSend);
      await reloadRef.current();
    } catch (err: any) {
      console.error('Error inviting member:', err);
      addMemberModalSetPatch({
        createError: err.message || 'Failed to invite member',
        isCreating: false,
      });
    }
  };

  if (!snap.open) {
    return createPortal(
      <div id="add-member-modal-layer" className="pointer-events-none fixed inset-0 z-[200] overflow-hidden opacity-0" aria-hidden />,
      document.body
    );
  }

  return createPortal(
    <div
      id="add-member-modal-layer"
      className="mm-invite-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-member-dialog-title"
        className="mm-invite-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={close} aria-label="Close">
          <X width={16} height={16} aria-hidden />
        </button>

        {snap.modal.step === 'form' && (
          <form onSubmit={handleSubmit}>
            <h2 id="add-member-dialog-title" className="modal-title">
              Invite a member
            </h2>
            <p className="modal-sub">
              Send an email invite. They’ll appear under <strong>Pending</strong> until they accept.
            </p>
            <label htmlFor="memberEmail" className="field-label">
              Email
            </label>
            <input
              ref={emailInputRef}
              type="email"
              id="memberEmail"
              value={snap.memberEmail}
              onChange={(e) => {
                setInvalid(false);
                addMemberModalSetForm(e.target.value);
              }}
              placeholder="name@example.com"
              className={`modal-input${invalid ? ' invalid' : ''}`}
              autoComplete="off"
              disabled={snap.isCreating}
            />
            {invalid && <p className="field-error">Enter a valid email address.</p>}
            {snap.createError && <p className="field-error">{snap.createError}</p>}
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={close} disabled={snap.isCreating}>
                Cancel
              </button>
              <button
                type="submit"
                className={`btn-primary${snap.isCreating ? ' sending' : ''}`}
                disabled={snap.isCreating}
              >
                {snap.isCreating ? (
                  <>
                    <span className="spinner" />
                    Sending…
                  </>
                ) : (
                  'Send invite'
                )}
              </button>
            </div>
          </form>
        )}

        {snap.modal.step === 'success' && (
          <div>
            <h2 id="add-member-dialog-title" className="modal-title">
              Invite sent
            </h2>
            <p className="modal-sub">
              An invite email is on its way to <strong>{snap.modal.email}</strong>. It’ll show up under Pending
              until they accept.
            </p>
            {snap.modal.showResendHint && (
              <p className="modal-sub">We sent another invitation to their inbox.</p>
            )}
            {snap.modal.emailWarning && <p className="field-error">{snap.modal.emailWarning}</p>}
            <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn-primary mm-invite-done" onClick={close}>
                Done
              </button>
            </div>
          </div>
        )}

        {snap.modal.step === 'already_connected' && (
          <div>
            <h2 id="add-member-dialog-title" className="modal-title">
              Already connected
            </h2>
            <p className="modal-sub">
              You’re already connected with <strong>{snap.modal.email}</strong>.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={close}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
